import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Layers, Paperclip, X, Send, Loader2, CheckCircle, ExternalLink, Rocket, Search } from "lucide-react";

const PRIORIDADES = ["P1", "P2", "P3", "P4"];

const TRACKS = [
  { id: "fast_track", label: "Fast track", icon: Zap, color: "#f59e0b",
    desc: "Necesidades pequeñas y urgentes. SLA de respuesta corto." },
  { id: "desarrollo", label: "Desarrollo", icon: Layers, color: "#007934",
    desc: "Proyectos o mejoras que requieren planificación en sprint." },
];

const INICIATIVA_MODES = [
  { id: "none", label: "Sin iniciativa" },
  { id: "link_existing", label: "Vincular a iniciativa existente" },
  { id: "create_new", label: "Crear nueva iniciativa" },
];

export default function SolicitudDemanda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState([]);
  const [track, setTrack] = useState(searchParams.get("track") === "desarrollo" ? "desarrollo" : "fast_track");
  const [titulo, setTitulo] = useState(searchParams.get("titulo") || "");
  const [descripcion, setDescripcion] = useState(searchParams.get("descripcion") || "");
  const [prioridad, setPrioridad] = useState("P3");
  const [areaNegocio, setAreaNegocio] = useState("");
  const [adoProject, setAdoProject] = useState("");
  const [files, setFiles] = useState([]);
  const [iniciativaMode, setIniciativaMode] = useState("none");
  const [iniciativaQuery, setIniciativaQuery] = useState("");
  const [iniciativaResults, setIniciativaResults] = useState([]);
  const [iniciativaSelected, setIniciativaSelected] = useState(null);
  const [iniciativaTitulo, setIniciativaTitulo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // Iter313 · O08 — aviso de posible duplicado antes de crear otra solicitud igual
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: pr }] = await Promise.all([
          axios.get(`${API}/demanda/config`),
          axios.get(`${API}/demanda/ado-projects`),
        ]);
        setConfig(cfg);
        setAdoProject(cfg.ado_project);
        setProjects(pr.projects || []);
      } catch {
        toast.error("Error cargando configuración de Demanda");
      }
    })();
  }, []);

  useEffect(() => {
    if (iniciativaMode !== "link_existing" || iniciativaQuery.length < 2) { setIniciativaResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/demanda/iniciativas`, { params: { q: iniciativaQuery, ado_project: adoProject } })
        .then(r => setIniciativaResults(r.data.items || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [iniciativaMode, iniciativaQuery, adoProject]);

  const handleSubmit = async (confirmDuplicate = false) => {
    if (!titulo.trim()) return setError("El título es obligatorio");
    setError("");
    setDuplicateWarning(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/demanda/solicitudes`, {
        track, titulo, descripcion, prioridad, area_negocio: areaNegocio, ado_project: adoProject,
        iniciativa_mode: iniciativaMode,
        iniciativa_id: iniciativaMode === "link_existing" ? iniciativaSelected?.id : null,
        iniciativa_titulo: iniciativaMode === "create_new" ? iniciativaTitulo : null,
        confirm_duplicate: confirmDuplicate,
      });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/demanda/solicitudes/${data.id}/attachments`, fd);
      }
      setResult(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.existing) {
        setDuplicateWarning(detail);
      } else {
        setError(detail || "No se pudo crear la solicitud");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4" data-testid="solicitud-demanda-success">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-[#f0fdf4] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-[#007934]" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Solicitud enviada</h2>
          <p className="text-gray-500 text-sm mb-4">{result.titulo}</p>
          <div className="flex justify-center gap-2 mb-4">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{result.track === "fast_track" ? "Fast track" : "Desarrollo"}</span>
            {result.sla_due_at && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">SLA: {new Date(result.sla_due_at).toLocaleString("es-ES")}</span>}
          </div>
          {result.iniciativa_titulo && (
            <p className="text-xs text-gray-500 mb-3" data-testid="result-iniciativa">
              <Rocket size={11} className="inline mr-1" />Iniciativa: {result.iniciativa_titulo}{result.iniciativa_azure_id ? ` (#${result.iniciativa_azure_id})` : ""}
            </p>
          )}
          {result.azure_id ? (
            <a href={result.azure_url} target="_blank" rel="noreferrer" data-testid="azure-wi-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#007934] hover:underline">
              Ver Work Item #{result.azure_id} en Azure DevOps <ExternalLink size={13} />
            </a>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
              Registrado localmente. Pendiente de creación en Azure DevOps ({result.ado_error || "en cola"}).
            </div>
          )}
          <div className="flex justify-center gap-3 mt-6">
            <button onClick={() => navigate(`/seguimiento-demanda/${result.id}`)} data-testid="ver-seguimiento-btn"
              className="text-sm font-semibold text-gray-600 hover:text-gray-900">Ver seguimiento</button>
            <button onClick={() => { setResult(null); setTitulo(""); setDescripcion(""); setFiles([]); }}
              className="text-sm font-semibold text-[#007934] hover:underline">Nueva solicitud</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5" data-testid="solicitud-demanda-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nueva solicitud</h1>
        <p className="text-sm text-gray-500 mt-0.5">Envía una necesidad a Plataformas Corporativas TIC — se convierte en un work item en Azure DevOps</p>
      </div>

      {searchParams.get("titulo") && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-xs text-indigo-700" data-testid="prellenado-desde-peticion-banner">
          Pre-rellenado desde una Petición IT — revisa los datos antes de enviar.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Track *</p>
          <div className="grid grid-cols-2 gap-3">
            {TRACKS.map(t => (
              <button key={t.id} type="button" onClick={() => setTrack(t.id)} data-testid={`track-card-${t.id}`}
                className={`text-left p-4 rounded-xl border-2 transition-all ${track === t.id ? "border-[#007934] bg-[#f0fdf4]" : "border-gray-100 hover:border-gray-200"}`}>
                <t.icon size={18} style={{ color: t.color }} className="mb-2" />
                <p className="font-bold text-sm text-gray-900">{t.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Título *</label>
          <input value={titulo} onChange={e => setTitulo(e.target.value)} data-testid="input-titulo-demanda"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]"
            placeholder="Ej: Portal de fichaje no permite exportar CSV" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Descripción</label>
          <textarea rows={4} value={descripcion} onChange={e => setDescripcion(e.target.value)} data-testid="input-descripcion-demanda"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934] resize-none"
            placeholder="Describe la necesidad, contexto y qué resultado esperas..." />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Prioridad</label>
            <select value={prioridad} onChange={e => setPrioridad(e.target.value)} data-testid="select-prioridad-demanda"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]">
              {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Proyecto ADO</label>
            <select value={adoProject} onChange={e => setAdoProject(e.target.value)} data-testid="select-ado-project-demanda"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]">
              {!projects.find(p => p.name === adoProject) && adoProject && <option value={adoProject}>{adoProject}</option>}
              {projects.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Área / negocio</label>
          <input value={areaNegocio} onChange={e => setAreaNegocio(e.target.value)} data-testid="input-area-negocio-demanda"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]"
            placeholder="Ej: Recursos Humanos, Operaciones Zona Sur..." />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Solicitante</label>
          <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-3 py-2.5">{user?.name || user?.email}</p>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block flex items-center gap-1.5"><Rocket size={12} /> Iniciativa (opcional)</label>
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            {INICIATIVA_MODES.map(m => (
              <button key={m.id} type="button" onClick={() => setIniciativaMode(m.id)} data-testid={`iniciativa-mode-${m.id}`}
                className={`text-[11px] font-semibold px-2 py-2 rounded-lg border ${iniciativaMode === m.id ? "border-[#007934] bg-[#f0fdf4] text-[#007934]" : "border-gray-200 text-gray-500"}`}>
                {m.label}
              </button>
            ))}
          </div>
          {iniciativaMode === "link_existing" && (
            <div className="relative">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={iniciativaSelected ? iniciativaSelected.title : iniciativaQuery}
                  data-testid="input-iniciativa-search"
                  onChange={e => { setIniciativaSelected(null); setIniciativaQuery(e.target.value); }}
                  placeholder="Buscar Epic existente por título..."
                  className="w-full pl-8 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              {iniciativaResults.length > 0 && !iniciativaSelected && (
                <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden">
                  {iniciativaResults.map(r => (
                    <button key={r.id} type="button" data-testid={`iniciativa-option-${r.id}`}
                      onClick={() => setIniciativaSelected(r)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#f0fdf4] border-b border-gray-50 last:border-0">
                      {r.title} {r.azure_id && <span className="text-gray-400">#{r.azure_id}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {iniciativaMode === "create_new" && (
            <input value={iniciativaTitulo} onChange={e => setIniciativaTitulo(e.target.value)} data-testid="input-iniciativa-nueva"
              placeholder="Título de la nueva iniciativa (Epic)"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" />
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Adjuntos (opcional)</label>
          <label className="flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-xl px-3 py-3 text-sm text-gray-400 cursor-pointer hover:border-[#007934] hover:text-[#007934]">
            <Paperclip size={14} /> Añadir archivo
            <input type="file" className="hidden" data-testid="input-adjuntos-demanda"
              onChange={e => setFiles(f => [...f, ...Array.from(e.target.files || [])])} />
          </label>
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                  <span className="text-gray-600 truncate">{f.name}</span>
                  <button onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}><X size={12} className="text-gray-400" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <p className="text-red-500 text-sm" data-testid="error-solicitud-demanda">{error}</p>}

        {/* Iter313 · O08 — aviso de posible duplicado */}
        {duplicateWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2" data-testid="duplicate-warning">
            <p className="text-sm text-amber-800 font-medium">⚠ {duplicateWarning.message}</p>
            <p className="text-xs text-amber-700">
              Solicitud existente: <b>"{duplicateWarning.existing.titulo}"</b> ({duplicateWarning.existing.status}) — {Math.round(duplicateWarning.similarity * 100)}% similar
            </p>
            <div className="flex gap-2">
              <button onClick={() => handleSubmit(true)} disabled={submitting}
                className="text-xs font-semibold bg-amber-600 text-white rounded-lg px-3 py-1.5 hover:bg-amber-700" data-testid="duplicate-confirm-create">
                Sí, es una petición distinta — crear igualmente
              </button>
              <button onClick={() => setDuplicateWarning(null)}
                className="text-xs font-semibold text-amber-700 border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-100" data-testid="duplicate-cancel">
                Cancelar
              </button>
            </div>
          </div>
        )}

        <button onClick={() => handleSubmit(false)} disabled={submitting} data-testid="submit-solicitud-demanda"
          className="w-full bg-[#007934] text-white rounded-xl px-6 py-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#006228] transition-colors disabled:opacity-60">
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Enviando...</> : <><Send size={15} /> Enviar solicitud</>}
        </button>
      </div>
    </div>
  );
}
